/**
 * FormValidator - A drop-in replacement for Syncfusion's FormValidator
 * Provides form validation functionality using HTML5 validation and custom rules
 */

// Support both tuple and array formats for Syncfusion compatibility
type RuleTuple<T> = [T, string?] | (T | string)[];
/** The three tags `attachEvents` binds to; all of them carry `name`. */
type FormControlElement = HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;
type CustomValidator = (value: string, element: HTMLInputElement) => boolean;
// Invoked as `validator({ value, element })` by `validateField` below.
type RangeValidator = (args: { value: string; element: HTMLInputElement }) => boolean;

export interface ValidationRule {
  required?: RuleTuple<boolean>;
  email?: RuleTuple<boolean>;
  minLength?: RuleTuple<number>;
  maxLength?: RuleTuple<number>;
  min?: RuleTuple<number>;
  max?: RuleTuple<number>;
  pattern?: RuleTuple<string | RegExp>;
  regex?: RuleTuple<string | RegExp>; // Alias for pattern (Syncfusion compatibility)
  equalTo?: RuleTuple<string>;
  range?: RuleTuple<string | RangeValidator>; // For custom range/equalTo validation
  custom?: RuleTuple<CustomValidator>;
}

export interface ValidationRules {
  [fieldName: string]: ValidationRule;
}

/** Every key of `ValidationRule`, so `removeRules` can check a caller-supplied name against it. */
const VALIDATION_RULE_NAMES: readonly (keyof ValidationRule)[] = [
  "required",
  "email",
  "minLength",
  "maxLength",
  "min",
  "max",
  "pattern",
  "regex",
  "equalTo",
  "range",
  "custom",
];

const isValidationRuleName = (name: string): name is keyof ValidationRule =>
  VALIDATION_RULE_NAMES.some((ruleName) => ruleName === name);

/*
 * A rule reaches `validateField` either as a real `[value, message?]` tuple or — from the call
 * sites that build their options object without a type annotation, e.g. `{ required: [true, "…"] }`
 * — as an array widened to `(T | string)[]`, where both slots carry the same union. Both shapes are
 * read positionally: slot 0 is the rule value, slot 1 the optional message. These three read the
 * slots for what they are actually used as, instead of asserting the widened array back to a tuple.
 */

const ruleMessage = (rule: RuleTuple<unknown>): string | undefined =>
  typeof rule[1] === "string" ? rule[1] : undefined;

/** Slot 0 of a boolean rule is only ever read for truthiness. */
const isRuleEnabled = (rule: RuleTuple<boolean>): boolean => Boolean(rule[0]);

/** Slot 0 of a numeric rule; the comparisons below coerce it anyway, so convert rather than assert. */
const ruleLimit = (rule: RuleTuple<number>): number => Number(rule[0]);

/**
 * Slot 0 of a pattern rule. The return type admits `undefined` because the array form of
 * `RuleTuple` accepts an empty array — `[]` type-checks as `(T | string)[]` — which index access
 * does not model, so a rule that carries no pattern reaches this at runtime.
 */
const rulePattern = (rule: RuleTuple<string | RegExp>): string | RegExp | undefined => rule[0];

export interface FormValidatorOptions {
  rules?: ValidationRules;
  validationComplete?: (args: { status: string; errorElement?: HTMLElement; inputElement?: HTMLElement }) => void;
  customPlacement?: (inputElement: HTMLInputElement, errorElement: HTMLElement) => void;
  focusout?: (args: { element: HTMLElement }) => void;
  keyup?: (args: { element: HTMLElement }) => void;
  click?: (args: { element: HTMLElement }) => void;
  change?: (args: { element: HTMLElement }) => void;
  submit?: (args: { event: Event }) => void;
}

export class FormValidator {
  /*
   * NOT an `HTMLFormElement`: every `new FormValidator(...)` in the repo points at the container
   * `<div>` that holds the fields (`#popForm`, `#formData`, `#signinForm`, …), never at a `<form>`.
   * Only `querySelector`/`querySelectorAll` are used on it, which any element supports.
   */
  private formElement: HTMLElement | null = null;
  private rules: ValidationRules = {};
  private errors: Map<string, string> = new Map();
  private options: FormValidatorOptions;

  constructor(formSelector: string | HTMLElement, options: FormValidatorOptions = {}) {
    this.options = options;
    this.rules = options.rules || {};

    if (typeof formSelector === "string") {
      // Stays nullable: a selector that matches nothing has to leave the validator inert, which is
      // what the `if (this.formElement)` below has always relied on at runtime.
      this.formElement = document.querySelector<HTMLElement>(formSelector);
    } else {
      this.formElement = formSelector;
    }

    if (this.formElement) {
      this.attachEvents();
    }
  }

  private attachEvents(): void {
    if (!this.formElement) return;

    // Prevent default form submission
    this.formElement.addEventListener("submit", (e) => {
      e.preventDefault();
      if (this.options.submit) {
        this.options.submit({ event: e });
      }
    });

    // Attach events to inputs. The element type comes from the selector itself rather than from an
    // assertion — a `select` or a `textarea` is not an `HTMLInputElement`. Listeners are registered
    // through the shared `HTMLElement` base, which is also the type the callbacks hand back.
    const inputs = this.formElement.querySelectorAll<FormControlElement>("input, select, textarea");
    inputs.forEach((control) => {
      const element: HTMLElement = control;

      if (this.options.focusout) {
        element.addEventListener("blur", () => {
          this.validateField(control.name);
          this.options.focusout!({ element });
        });
      }

      if (this.options.keyup) {
        element.addEventListener("keyup", () => {
          this.options.keyup!({ element });
        });
      }

      if (this.options.change) {
        element.addEventListener("change", () => {
          this.validateField(control.name);
          this.options.change!({ element });
        });
      }

      if (this.options.click) {
        element.addEventListener("click", () => {
          this.options.click!({ element });
        });
      }
    });
  }

  /**
   * Add validation rules for a field
   */
  addRules(fieldName: string, rules: ValidationRule): void {
    this.rules[fieldName] = { ...this.rules[fieldName], ...rules };
  }

  /**
   * Remove validation rules for a field
   */
  removeRules(fieldName: string, ruleNames?: string[]): void {
    if (!ruleNames) {
      delete this.rules[fieldName];
      return;
    }
    const fieldRules = this.rules[fieldName];
    if (!fieldRules) return;
    // A name that is not a `ValidationRule` key never named a rule on the bag in the first place.
    ruleNames.forEach((ruleName) => {
      if (isValidationRuleName(ruleName)) {
        delete fieldRules[ruleName];
      }
    });
  }

  /**
   * Validate a single field
   */
  validateField(fieldName: string): boolean {
    if (!this.formElement) return true;

    // Nullable on purpose: a field can carry rules while its control is not rendered, and such a
    // field has to count as valid rather than block the form.
    const input = this.formElement.querySelector<HTMLInputElement>(`[name="${fieldName}"]`);
    if (!input) return true;

    const fieldRules = this.rules[fieldName];
    if (!fieldRules) return true;

    const value = input.value;
    let isValid = true;
    let errorMessage = "";

    // Required validation
    if (fieldRules.required) {
      if (isRuleEnabled(fieldRules.required) && !value.trim()) {
        isValid = false;
        errorMessage = ruleMessage(fieldRules.required) || "This field is required";
      }
    }

    // Email validation
    if (isValid && fieldRules.email) {
      if (isRuleEnabled(fieldRules.email) && value) {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(value)) {
          isValid = false;
          errorMessage = ruleMessage(fieldRules.email) || "Please enter a valid email address";
        }
      }
    }

    // Min length validation
    if (isValid && fieldRules.minLength) {
      const minLen = ruleLimit(fieldRules.minLength);
      if (value.length < minLen) {
        isValid = false;
        errorMessage = ruleMessage(fieldRules.minLength) || `Minimum ${minLen} characters required`;
      }
    }

    // Max length validation
    if (isValid && fieldRules.maxLength) {
      const maxLen = ruleLimit(fieldRules.maxLength);
      if (value.length > maxLen) {
        isValid = false;
        errorMessage = ruleMessage(fieldRules.maxLength) || `Maximum ${maxLen} characters allowed`;
      }
    }

    // Min value validation
    if (isValid && fieldRules.min) {
      const minVal = ruleLimit(fieldRules.min);
      const numValue = parseFloat(value);
      if (!isNaN(numValue) && numValue < minVal) {
        isValid = false;
        errorMessage = ruleMessage(fieldRules.min) || `Minimum value is ${minVal}`;
      }
    }

    // Max value validation
    if (isValid && fieldRules.max) {
      const maxVal = ruleLimit(fieldRules.max);
      const numValue = parseFloat(value);
      if (!isNaN(numValue) && numValue > maxVal) {
        isValid = false;
        errorMessage = ruleMessage(fieldRules.max) || `Maximum value is ${maxVal}`;
      }
    }

    // Pattern validation (also supports 'regex' alias)
    const patternRule = fieldRules.pattern || fieldRules.regex;
    if (isValid && patternRule) {
      const pattern = rulePattern(patternRule);
      // An empty rule array carries no pattern to test against, so there is nothing to fail.
      if (pattern !== undefined) {
        const regex = typeof pattern === "string" ? new RegExp(pattern) : pattern;
        if (value && !regex.test(value)) {
          isValid = false;
          errorMessage = ruleMessage(patternRule) || "Please match the requested format";
        }
      }
    }

    // Equal to validation
    if (isValid && fieldRules.equalTo) {
      const targetFieldName = fieldRules.equalTo[0];
      // Nullable for the same reason as `input` above: the field being compared against may not be
      // on screen, and then there is nothing to compare.
      const targetInput = this.formElement.querySelector<HTMLInputElement>(`[name="${targetFieldName}"]`);
      if (targetInput && value !== targetInput.value) {
        isValid = false;
        errorMessage = ruleMessage(fieldRules.equalTo) || "Values do not match";
      }
    }

    // Range validation (Syncfusion uses this for custom validators like password match)
    if (isValid && fieldRules.range) {
      const validator = fieldRules.range[0];
      if (typeof validator === "function") {
        if (!validator({ value, element: input })) {
          isValid = false;
          errorMessage = ruleMessage(fieldRules.range) || "Invalid value";
        }
      }
    }

    // Custom validation
    if (isValid && fieldRules.custom) {
      const validator = fieldRules.custom[0];
      if (typeof validator === "function" && !validator(value, input)) {
        isValid = false;
        errorMessage = ruleMessage(fieldRules.custom) || "Invalid value";
      }
    }

    // Update error state
    if (!isValid) {
      this.errors.set(fieldName, errorMessage);
      this.showError(input, errorMessage);
    } else {
      this.errors.delete(fieldName);
      this.hideError(input);
    }

    // Callback
    if (this.options.validationComplete) {
      this.options.validationComplete({
        status: isValid ? "success" : "failure",
        inputElement: input,
      });
    }

    return isValid;
  }

  /**
   * Validate all fields in the form
   */
  validate(): boolean {
    let isFormValid = true;

    Object.keys(this.rules).forEach((fieldName) => {
      const isFieldValid = this.validateField(fieldName);
      if (!isFieldValid) {
        isFormValid = false;
      }
    });

    return isFormValid;
  }

  /**
   * Get error messages
   */
  getErrors(): Record<string, string> {
    const errors: Record<string, string> = {};
    this.errors.forEach((value, key) => {
      errors[key] = value;
    });
    return errors;
  }

  /**
   * Reset all validation errors
   */
  reset(): void {
    this.errors.clear();
    if (this.formElement) {
      const errorElements = this.formElement.querySelectorAll(".e-error, .validation-error");
      errorElements.forEach((el) => el.remove());

      const inputs = this.formElement.querySelectorAll("input, select, textarea");
      inputs.forEach((input) => {
        input.classList.remove("e-error", "is-invalid");
      });
    }
  }

  /**
   * Destroy the validator
   */
  destroy(): void {
    this.reset();
    this.rules = {};
    this.formElement = null;
  }

  private showError(input: HTMLInputElement, message: string): void {
    input.classList.add("e-error", "is-invalid");

    // Remove existing error message
    const existingError = input.parentElement?.querySelector(".validation-error");
    if (existingError) {
      existingError.textContent = message;
      return;
    }

    // Create error element
    const errorElement = document.createElement("span");
    errorElement.className = "validation-error e-error";
    errorElement.style.cssText = "color: #d32f2f; font-size: 0.75rem; margin-top: 0.25rem; display: block;";
    errorElement.textContent = message;

    if (this.options.customPlacement) {
      this.options.customPlacement(input, errorElement);
    } else {
      input.parentElement?.appendChild(errorElement);
    }
  }

  private hideError(input: HTMLInputElement): void {
    input.classList.remove("e-error", "is-invalid");

    const errorElement = input.parentElement?.querySelector(".validation-error");
    if (errorElement) {
      errorElement.remove();
    }
  }
}

export default FormValidator;
